import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import styles from "./Professions.module.scss";
import Spinner from "../../components/spinner";
import { useLanguage } from "../../context/languageContext";
import { Can, usePermissions } from "../../context/permissionContext";
import { confirmCascadeDelete } from "../../helper/confirmCascadeDelete";
import { useAlreadyExists } from "../../components/confirmDialog";
import GenerateCSV from "../../components/popup/generateCSV";
import { BsFiletypeCsv } from "react-icons/bs";

const API = process.env.REACT_APP_API_ROOT;

/**
 * The professions catalogue — the list offered by Profession & Field on the
 * Author and Recommender forms.
 *
 * It used to be a JSON file bundled into the build, so adding one meant a
 * deploy. Editing here changes what those pickers offer immediately.
 *
 * Adding takes English only; the other five are machine-translated on save.
 * Editing opens all six, so a poor translation can be corrected by hand —
 * these names are what the app's Picks chips show in each language. Changing
 * the English name re-translates only the languages not edited here.
 *
 * The list is also the ORDER of the profession chips on the app's Picks tab:
 * drag rows, then Save order. Professions nobody has are listed but hidden in
 * the app, which is why each row says how many people carry it.
 */

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "es", label: "Spanish" },
  { code: "ja", label: "Japanese" },
];

const namesOf = (profession) =>
  Object.fromEntries(LANGS.map(({ code }) => [code, profession.professionName?.[code] || ""]));

/** The app's order: placed rows by `order`, the rest busiest first. */
const byAppOrder = (a, b) => {
  const ao = Number.isFinite(a.order) ? a.order : null;
  const bo = Number.isFinite(b.order) ? b.order : null;
  if (ao !== null && bo !== null) return ao - bo;
  if (ao !== null) return -1;
  if (bo !== null) return 1;
  return (b.authorCount || 0) - (a.authorCount || 0)
    || (a.professionName?.en || "").localeCompare(b.professionName?.en || "");
};

/** One row's drag handle and transform; the row's content is passed in. */
function SortableRow({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });

  return (
    <li
      ref={setNodeRef}
      className={`${styles.row} ${isDragging ? styles.dragging : ""}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {!disabled && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          className={styles.handle}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>
      )}
      {children}
    </li>
  );
}
const Professions = () => {
  const { adminInfo, isShowCSVModalVisible, setIsShowCSVModalVisible, handleCloseCSV, handleGenerateCSV } = useLanguage();
  const showAlreadyExists = useAlreadyExists();
  const { can } = usePermissions();

  const [professions, setProfessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState("");
  // `editing`: null = adding, otherwise the id being edited. `editDraft` holds
  // all six names; `editOriginal` is what they were, to send only changes.
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editOriginal, setEditOriginal] = useState({});
  // True once rows have been dragged and not yet saved.
  const [orderDirty, setOrderDirty] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const headers = { Authorization: `Bearer ${adminInfo?.token}` };

  const fetchProfessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/professions/getall`, { headers });

      if (data?.status) {
        setProfessions([...(data.data?.professions || [])].sort(byAppOrder));
        setOrderDirty(false);
      }
    } catch (error) {
      console.error("Error fetching professions:", error);
      toast.error("Could not load professions.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminInfo?.token]);

  useEffect(() => {
    if (!adminInfo?.token) return;

    document.title = "Penspace - Professions";
    fetchProfessions();
  }, [adminInfo?.token, fetchProfessions]);

  const handleAdd = async () => {
    const name = draft.trim();

    if (!name || saving) return;

    setSaving(true);

    try {
      await axios.post(
        `${API}/api/admin/professions/add`,
        { professionName: { en: name } },
        { headers }
      );
      setDraft("");
      toast.success(`${name} added.`);
      await fetchProfessions();
    } catch (error) {
      if (!showAlreadyExists(error)) toast.error(error?.response?.data?.error || "Could not add that profession.");
    } finally {
      setSaving(false);
    }
  };

  // Addressed by `professionId`. The seeded entries have no subdocument
  // `_id`, so keying on that gave every one of them `undefined` — one shared
  // identity, and the whole list opened for editing at once.
  const startEditing = (profession) => {
    setEditing(profession.professionId);
    setEditDraft(namesOf(profession));
    setEditOriginal(namesOf(profession));
  };

  // Sends English plus only the languages actually edited. The backend saves
  // those as typed and, if English changed, re-translates the rest — sending
  // all six would have frozen the old translations of a renamed profession.
  const handleSave = async (id) => {
    const next = Object.fromEntries(LANGS.map(({ code }) => [code, (editDraft[code] || "").trim()]));

    if (!next.en || saving) return;

    const edited = LANGS.filter(
      ({ code }) => code !== "en" && next[code] && next[code] !== (editOriginal[code] || "").trim()
    ).map(({ code }) => code);

    if (next.en === (editOriginal.en || "").trim() && edited.length === 0) {
      setEditing(null);
      return;
    }

    const professionName = { en: next.en };
    edited.forEach((code) => {
      professionName[code] = next[code];
    });

    setSaving(true);

    try {
      const { data } = await axios.put(
        `${API}/api/admin/professions/update/${id}`,
        { professionName },
        { headers }
      );
      setEditing(null);
      if (data?.untranslated?.length) {
        toast.warn(data.message);
      } else {
        toast.success("Profession updated.");
      }
      await fetchProfessions();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not update that profession.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profession) => {
    const label = profession.professionName?.en || "this profession";

    // Deleting it also takes it off every author and recommender that has it,
    // which is worth saying before the click rather than after.
    if (!(await confirmCascadeDelete({ source: "profession", ids: [profession.professionId], what: `"${label}"`, headers }))) {
      return;
    }

    try {
      const { data: response } = await axios.put(
        `${API}/api/admin/professions/delete/${profession.professionId}`,
        {},
        { headers }
      );
      toast.success(response?.message || `${label} removed.`);
      await fetchProfessions();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not remove that profession.");
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setProfessions((rows) => {
      const from = rows.findIndex((row) => String(row.professionId) === String(active.id));
      const to = rows.findIndex((row) => String(row.professionId) === String(over.id));
      return from < 0 || to < 0 ? rows : arrayMove(rows, from, to);
    });
    setOrderDirty(true);
  };

  const handleSaveOrder = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/admin/professions/reorder`,
        { professionIds: professions.map((row) => row.professionId) },
        { headers }
      );
      toast.success("Chip order saved.");
      await fetchProfessions();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not save the order.");
    } finally {
      setSaving(false);
    }
  };

  const canReorder = can("professions", "edit");

  if (loading) return <Spinner />;

  return (
    <React.Fragment>
      <div className="heading">
        <div>
          <h4 className="mb-0">Professions ({professions.length})</h4>
          <small className="text-muted">
            The list offered by Profession &amp; Field on the Author and Recommender forms,
            and the order of the profession chips on the app's Picks tab — drag to reorder.
            The other five languages are translated on save.
          </small>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-dark" onClick={() => setIsShowCSVModalVisible(true)}>
            Generate CSV <BsFiletypeCsv />
          </button>
          {canReorder && (
            <button
              type="button"
              className="btn btn-dark"
              onClick={handleSaveOrder}
              disabled={!orderDirty || saving}
            >
              Save order
            </button>
          )}
        </div>
      </div>

      <div className={styles.panel}>
        <Can feature="professions" action="create">
          <div className={styles.addRow}>
            <input
              className="form_control"
              type="text"
              placeholder="Add a profession, in English…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <button
              type="button"
              className="btn btn-dark"
              onClick={handleAdd}
              disabled={!draft.trim() || saving}
            >
              <Plus size={16} /> Add
            </button>
          </div>
        </Can>

        {professions.length === 0 ? (
          <p className={styles.empty}>No professions yet — add the first one above.</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={professions.map((row) => String(row.professionId))}
            strategy={verticalListSortingStrategy}
          >
          <ul className={styles.list}>
            {professions.map((profession, index) => (
              <SortableRow
                key={profession.professionId}
                id={String(profession.professionId)}
                disabled={!canReorder || editing !== null}
              >
                {editing === profession.professionId ? (
                  <div className={styles.editor}>
                    <div className={styles.langGrid}>
                      {LANGS.map(({ code, label }) => (
                        <label key={code} className={styles.langField}>
                          <span className={styles.langLabel}>{label}</span>
                          <input
                            className="form_control"
                            type="text"
                            value={editDraft[code] || ""}
                            autoFocus={code === "en"}
                            lang={code}
                            onChange={(e) => setEditDraft((draft) => ({ ...draft, [code]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSave(profession.professionId);
                              if (e.key === "Escape") setEditing(null);
                            }}
                          />
                        </label>
                      ))}
                    </div>
                    <div className={styles.editorFooter}>
                      <small className="text-muted">
                        Changing English re-translates every language you haven't edited here.
                      </small>
                      <button
                        type="button"
                        className="btn btn-sm btn-dark"
                        onClick={() => handleSave(profession.professionId)}
                        disabled={saving || !(editDraft.en || "").trim()}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setEditing(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className={styles.position}>{index + 1}</span>
                    <span className={styles.name}>{profession.professionName?.en}</span>
                    {/* A chip for a profession nobody has would open an empty
                        list, so the app leaves those out. */}
                    <span
                      className={profession.authorCount ? styles.count : `${styles.count} ${styles.hidden}`}
                    >
                      {profession.authorCount
                        ? `${profession.authorCount} ${profession.authorCount === 1 ? "person" : "people"}`
                        : "Hidden in app — nobody has it"}
                    </span>
                    {/* Which languages have a name — hover one to read it. */}
                    <span className={styles.langs}>
                      {LANGS.filter(({ code }) => code !== "en" && profession.professionName?.[code]).length === 0
                        ? "English only"
                        : LANGS.filter(({ code }) => code !== "en" && profession.professionName?.[code]).map(
                            ({ code, label }, i) => (
                              <React.Fragment key={code}>
                                {i > 0 && " · "}
                                <span title={`${label}: ${profession.professionName[code]}`}>{code}</span>
                              </React.Fragment>
                            )
                          )}
                    </span>
                    {can("professions", "edit") && (
                      <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={`Edit ${profession.professionName?.en} in all languages`}
                        onClick={() => startEditing(profession)}
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {can("professions", "delete") && (
                      <button
                        type="button"
                        className={`${styles.iconButton} ${styles.danger}`}
                        aria-label={`Remove ${profession.professionName?.en}`}
                        onClick={() => handleDelete(profession)}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </>
                )}
              </SortableRow>
            ))}
          </ul>
          </SortableContext>
          </DndContext>
        )}
      </div>

      <GenerateCSV
        title="professions"
        isShowCSVModalVisible={isShowCSVModalVisible}
        handleCloseCSV={handleCloseCSV}
        handleGenerateCSV={() => handleGenerateCSV("professions")}
      />
    </React.Fragment>
  );
};

export default Professions;
